import { Add, Delete, Edit, Inventory2, MoreVert, Save, Search, TableChart } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box, Button, Divider, FormControl, IconButton, InputAdornment, InputLabel, ListItemIcon, ListItemText,
  Menu, MenuItem, Modal, Select, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import { AppPageHeader } from '../../Components/ui'
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel'
import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { getColumns } from '../../Components/ColumnsDefinitions/ProductsColumns'
import { CustomQuickFilter, CustomToolbarContainer, StyledDataGrid } from '../../Components/DataGrid'
import EmptyState from '../../Components/EmptyState'
import ModalHeader from '../../Components/Modal/ModalHeader'
import { Product } from '../../Model/ProductModel'
import { PriceTable } from '../../Model/PriceTableModel'
import { useDeleteProductsMutation, useFindAllProductsQuery, useUpdateProductsMutation } from '../../service/ProductService'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { updateProduct, clearAllDirty } from '../../store/reducers/product'
import { selectProducts, selectDirtyProducts, selectHasModifications } from '../../store/selectors/productSelectors'
import ModalCRUDPriceTable from './ModalCRUDPriceTable'
import ProductsModal from './ModalProducts'
import { useFindAllPlaylistsQuery } from '../../service/PlaylistService'
import ProductsHelper from './Helper'
import { useSyncPriceTables } from './useSyncPriceTables'

// Fase 3: schema de validação para edição inline
const productRowSchema = yup.object({
  name: yup.string().trim().min(1, 'Nome é obrigatório').required('Nome é obrigatório'),
  price: yup.number().min(0, 'Preço deve ser ≥ 0').nullable().optional(),
})

// Fase 5: SearchToolbar estabilizada fora do componente — evita que o DataGrid
// remonte a toolbar (e derrube o foco do campo de busca) a cada tecla digitada.
const SearchToolbar = () => (
  <CustomToolbarContainer sx={{ height: 48, display: 'flex', alignItems: 'center', px: 1 }}>
    <CustomQuickFilter placeholder="Buscar produto..." variant="filled" />
  </CustomToolbarContainer>
)

// Sentinel: "nenhuma tabela". Fora do componente para identity estável.
const notSelectTable: PriceTable = { id: null, userId: null, name: 'Nenhuma Tabela' }

type DesktopToolbarProps = {
  searchMode: 'livre' | 'exato'
  onChangeSearchMode: (mode: 'livre' | 'exato') => void
  search: string
  onChangeSearch: (value: string) => void
  tableFilter: number | 'all'
  onChangeTableFilter: (value: number | 'all') => void
  promoFilter: 'all' | 'promo' | 'no-promo'
  onChangePromoFilter: (value: 'all' | 'promo' | 'no-promo') => void
  priceTables: PriceTable[]
  hasModifications: boolean
  onSave: () => void
  isSaving: boolean
  hasSelection: boolean
  selectionCount: number
  onDelete: () => void
  isDeleting: boolean
  onBulkAssignTable: (tableId: number | null) => void
  onOpenImport: () => void
  onOpenCRUD: () => void
}

// Componente estável (fora do render de ProductsPage) — evita que o DataGrid
// remonte a toolbar (e derrube o foco do campo de busca) a cada tecla digitada.
const DesktopToolbar = ({
  searchMode, onChangeSearchMode, search, onChangeSearch, tableFilter, onChangeTableFilter,
  promoFilter, onChangePromoFilter, priceTables, hasModifications, onSave, isSaving,
  hasSelection, selectionCount, onDelete, isDeleting, onBulkAssignTable, onOpenImport, onOpenCRUD,
}: DesktopToolbarProps) => {
  const [assignAnchor, setAssignAnchor] = useState<null | HTMLElement>(null)

  return (
    <CustomToolbarContainer sx={{ height: 60, display: 'flex', alignItems: 'center', px: 1, gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Pesquisar por</InputLabel>
        <Select
          label="Pesquisar por"
          value={searchMode}
          onChange={(e) => onChangeSearchMode(e.target.value as 'livre' | 'exato')}
        >
          <MenuItem value="livre">Livre</MenuItem>
          <MenuItem value="exato">Código Exato</MenuItem>
        </Select>
      </FormControl>
      <TextField
        size="small"
        placeholder="Buscar itens..."
        value={search}
        onChange={(e) => onChangeSearch(e.target.value)}
        sx={{ flex: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
      />
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Tabela</InputLabel>
        <Select
          label="Tabela"
          value={tableFilter}
          onChange={(e) => onChangeTableFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}
        >
          <MenuItem value="all">Todas</MenuItem>
          {priceTables.map(t => (
            <MenuItem key={t.id} value={t.id ?? ''}>{t.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Promoção</InputLabel>
        <Select
          label="Promoção"
          value={promoFilter}
          onChange={(e) => onChangePromoFilter(e.target.value as 'all' | 'promo' | 'no-promo')}
        >
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="promo">Somente Promoção</MenuItem>
          <MenuItem value="no-promo">Sem Promoção</MenuItem>
        </Select>
      </FormControl>

      {/* Atribuir tabela em massa — visível quando há seleção */}
      {hasSelection && (
        <>
          <Button
            variant="outlined"
            onClick={e => setAssignAnchor(e.currentTarget)}
            startIcon={<TableChart sx={{ fontSize: 16 }} />}
            sx={{
              height: 32, fontSize: 13, px: 1.5, whiteSpace: 'nowrap',
              borderColor: 'rgba(20,184,160,0.5)', color: '#14b8a0',
              '&:hover': { borderColor: '#14b8a0', bgcolor: 'rgba(20,184,160,0.06)' },
            }}
          >
            Tabela ({selectionCount})
          </Button>
          <Menu
            anchorEl={assignAnchor}
            open={Boolean(assignAnchor)}
            onClose={() => setAssignAnchor(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            PaperProps={{
              sx: { borderRadius: '10px', minWidth: 200, maxHeight: 320, overflowY: 'auto' },
            }}
          >
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Atribuir tabela a {selectionCount} produto{selectionCount > 1 ? 's' : ''}
              </Typography>
            </Box>
            <Divider />
            {priceTables.map(t => (
              <MenuItem key={t.id} onClick={() => { setAssignAnchor(null); onBulkAssignTable(t.id) }} sx={{ py: 1, fontSize: 14 }}>
                {t.name}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem onClick={() => { setAssignAnchor(null); onBulkAssignTable(null) }} sx={{ py: 1, fontSize: 14, color: 'text.secondary' }}>
              Nenhuma tabela
            </MenuItem>
          </Menu>
        </>
      )}

      <LoadingButton color="success" variant="contained" disabled={!hasModifications}
        onClick={onSave} loading={isSaving} startIcon={<Save />}>
        Salvar
      </LoadingButton>
      <LoadingButton color="error" variant="contained" disabled={!hasSelection}
        loading={isDeleting} onClick={onDelete} startIcon={<Delete />}>
        Excluir
      </LoadingButton>
      <Button color="secondary" variant="contained" onClick={onOpenImport}>
        <Add sx={{ mr: '3px' }} /> Importar com txt
      </Button>
      <Button color="secondary" variant="contained" onClick={onOpenCRUD}>
        <Edit sx={{ mr: '3px' }} /> Minhas tabelas
      </Button>
    </CustomToolbarContainer>
  )
}

const ProductsPage = () => {
  const theme    = useTheme()
  const isDark   = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [openImport, setOpenImport]             = useState(false)
  const [openCRUD, setOpenCRUD]                 = useState(false)
  const [openHelp, setOpenHelp]                 = useState(false)
  const [moreAnchor, setMoreAnchor]             = useState<null | HTMLElement>(null)
  const [tableAnchor, setTableAnchor]           = useState<null | HTMLElement>(null)
  const [selectedProducts, setSelectedProducts] = useState<number[]>([])
  const [selectTables, setSelectTables]         = useState<PriceTable[]>([])
  const [searchMode, setSearchMode]             = useState<'livre' | 'exato'>('livre')
  const [search, setSearch]                     = useState('')
  const [tableFilter, setTableFilter]           = useState<number | 'all'>('all')
  const [promoFilter, setPromoFilter]           = useState<'all' | 'promo' | 'no-promo'>('all')

  const dispatch         = useAppDispatch()
  const products         = useAppSelector(selectProducts)
  const dirtyProducts    = useAppSelector(selectDirtyProducts)
  const hasModifications = useAppSelector(selectHasModifications)
  const user             = useAppSelector(state => state.auth.user)
  const priceTables      = useAppSelector(state => state.priceTable)

  useFindAllPlaylistsQuery(user?.id?.toString() || '')
  useFindAllProductsQuery(user?.id?.toString() || '', { refetchOnMountOrArgChange: true })
  const [deleteProducts, { isLoading: isDeleting }] = useDeleteProductsMutation()
  const [updateProducts, { isLoading: isUpdating }] = useUpdateProductsMutation()
  const syncPriceTables = useSyncPriceTables()

  // Fase 2: reset limpo — não acumular prev + priceTables a cada mudança
  useEffect(() => {
    setSelectTables([...priceTables, notSelectTable])
  }, [priceTables])

  async function handleDelete() { await deleteProducts(selectedProducts) }

  // Fase 5: handlers estáveis com useCallback (usados nas colunas memoizadas)
  const handleChangeProductTable = useCallback((row: Product, tableId: number | null) => {
    dispatch(updateProduct({ ...row, priceTableId: tableId }))
  }, [dispatch])

  const handleChangeProductHighlighted = useCallback((row: Product, highlighted: boolean) => {
    dispatch(updateProduct({ ...row, highlighted }))
  }, [dispatch])

  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectedProducts(model as number[])
  }, [])

  // Fase 3 + 5: validação Yup inline + handler estável
  const handleRowUpdate = useCallback(async (newRow: Product): Promise<Product> => {
    await productRowSchema.validate({ name: newRow.name, price: newRow.price })
    dispatch(updateProduct(newRow))
    return newRow
  }, [dispatch])

  const handleRowUpdateError = useCallback((error: unknown) => {
    const msg = error instanceof Error ? error.message : 'Valor inválido'
    toast.error(msg)
  }, [])

  function handleBulkAssignTable(tableId: number | null) {
    setTableAnchor(null)
    selectedProducts.forEach(productId => {
      const product = products.find(p => p.id === productId)
      if (product) dispatch(updateProduct({ ...product, priceTableId: tableId }))
    })
  }

  async function handleSave() {
    const result = await updateProducts(dirtyProducts)
    if ('error' in result) return
    dispatch(clearAllDirty())
    const affectedIds = new Set(
      dirtyProducts.map(p => p.priceTableId).filter((id): id is number => id != null)
    )
    await syncPriceTables(affectedIds)
  }

  // Fase 5: column callbacks estáveis → colunas só recalculam quando selectTables muda
  const columnCallbacks = useMemo(() => ({
    onChangeTable: handleChangeProductTable,
    onChangeHighlighted: handleChangeProductHighlighted,
  }), [handleChangeProductTable, handleChangeProductHighlighted])

  const [columnsDesktop, columnsMobile] = useMemo(
    () => getColumns(selectTables, columnCallbacks),
    [selectTables, columnCallbacks]
  )

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase()
    return products.filter(product => {
      if (term) {
        if (searchMode === 'exato') {
          if (String(product.idProduct) !== term) return false
        } else {
          const matchesName = product.name.toLowerCase().includes(term)
          const matchesCode = String(product.idProduct).includes(term)
          if (!matchesName && !matchesCode) return false
        }
      }
      if (tableFilter !== 'all' && product.priceTableId !== tableFilter) return false
      if (promoFilter === 'promo' && !product.highlighted) return false
      if (promoFilter === 'no-promo' && product.highlighted) return false
      return true
    })
  }, [products, search, searchMode, tableFilter, promoFilter])

  const bdr  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'
  const teal = isDark ? '#14b8a0' : '#0d9488'

  // Fase 4: overlay compartilhado entre mobile e desktop
  const NoRowsOverlay = useCallback(() => (
    <EmptyState icon={Inventory2} title="Nenhum produto cadastrado"
      subtitle="Importe um arquivo TXT ou adicione manualmente" />
  ), [])

  // Fase 4: props comuns aos dois DataGrids — evita duplicação e garante consistência
  const commonGridProps = {
    rows: filteredProducts,
    getRowId: (row: Product) => row.id as number,
    keepNonExistentRowsSelected: true,
    disableRowSelectionOnClick: true,
    rowSelectionModel: selectedProducts,
    onRowSelectionModelChange: handleSelectionChange,
    processRowUpdate: handleRowUpdate,
    onProcessRowUpdateError: handleRowUpdateError,
    checkboxSelection: true,
    sortingOrder: ['desc', 'asc'] as const,
    slots: { noRowsOverlay: NoRowsOverlay },
  }

  // ─── Modais compartilhados ────────────────────────────────────────────────
  const modals = (
    <>
      <ModalCRUDPriceTable open={openCRUD} onClose={() => setOpenCRUD(false)} />
      <ProductsModal productState={products} open={openImport} handleClose={() => setOpenImport(false)} />
      <Modal open={openHelp} onClose={() => setOpenHelp(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          bgcolor: 'background.paper', borderRadius: 2, p: 4,
          maxWidth: 560, width: '90%', maxHeight: '85vh', overflowY: 'auto',
        }}>
          <ModalHeader title="Produtos" onClose={() => setOpenHelp(false)} />
          <ProductsHelper />
        </Box>
      </Modal>
    </>
  )

  // ─── Mobile ──────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {modals}

        {/* Header compacto */}
        <Box sx={{
          px: 2, py: 1.25,
          borderBottom: `1px solid ${bdr}`,
          display: 'flex', alignItems: 'center', gap: 1,
          minHeight: 48, flexShrink: 0,
          bgcolor: 'background.default',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Produtos
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1 }}>
              {products.length} itens
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Atribuir tabela — visível quando há seleção */}
          {selectedProducts.length > 0 && (
            <>
              <Button
                size="small" variant="outlined"
                onClick={e => setTableAnchor(e.currentTarget)}
                startIcon={<TableChart sx={{ fontSize: 15 }} />}
                sx={{
                  height: 32, fontSize: 12, px: 1.5, borderRadius: '8px',
                  borderColor: isDark ? 'rgba(20,184,160,0.4)' : 'rgba(13,148,136,0.4)',
                  color: teal,
                }}
              >
                Tabela ({selectedProducts.length})
              </Button>

              <Menu
                anchorEl={tableAnchor}
                open={Boolean(tableAnchor)}
                onClose={() => setTableAnchor(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    borderRadius: '12px', minWidth: 180, maxHeight: 320,
                    boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                    border: `1px solid ${bdr}`,
                    overflowY: 'auto',
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${bdr}` }}>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Atribuir a tabela
                  </Typography>
                </Box>
                {priceTables.map(t => (
                  <MenuItem key={t.id} onClick={() => handleBulkAssignTable(t.id)} sx={{ py: 1.25, px: 2 }}>
                    <ListItemText primary={<Typography sx={{ fontSize: 14 }}>{t.name}</Typography>} />
                  </MenuItem>
                ))}
                <MenuItem onClick={() => handleBulkAssignTable(null)}
                  sx={{ py: 1.25, px: 2, borderTop: `1px solid ${bdr}` }}>
                  <ListItemText primary={<Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Nenhuma tabela</Typography>} />
                </MenuItem>
              </Menu>
            </>
          )}

          {/* Salvar — visível só quando há modificações */}
          {hasModifications && (
            <LoadingButton
              size="small" color="success" variant="contained"
              loading={isUpdating} onClick={handleSave}
              startIcon={<Save />}
              sx={{ height: 32, fontSize: 12, px: 1.5, borderRadius: '8px' }}
            >
              Salvar
            </LoadingButton>
          )}

          {/* Excluir — visível só quando há seleção */}
          {selectedProducts.length > 0 && (
            <LoadingButton
              size="small" color="error" variant="outlined"
              loading={isDeleting} onClick={handleDelete}
              sx={{ height: 32, minWidth: 36, px: 1, borderRadius: '8px', borderColor: 'error.main' }}
            >
              <Delete sx={{ fontSize: 16 }} />
            </LoadingButton>
          )}

          {/* Menu mais opções */}
          <IconButton size="small" onClick={e => setMoreAnchor(e.currentTarget)}
            sx={{ width: 36, height: 36, color: 'text.disabled' }}>
            <MoreVert sx={{ fontSize: 18 }} />
          </IconButton>

          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                borderRadius: '12px', minWidth: 200,
                boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${bdr}`,
              },
            }}
          >
            <MenuItem onClick={() => { setMoreAnchor(null); setOpenImport(true) }} sx={{ py: 1.25, px: 2 }}>
              <ListItemIcon><Add sx={{ fontSize: 17, color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Importar com TXT</Typography>} />
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); setOpenCRUD(true) }} sx={{ py: 1.25, px: 2 }}>
              <ListItemIcon><TableChart sx={{ fontSize: 17, color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Minhas Tabelas</Typography>} />
            </MenuItem>
            <MenuItem onClick={() => { setMoreAnchor(null); setOpenHelp(true) }} sx={{ py: 1.25, px: 2 }}>
              <ListItemIcon><Edit sx={{ fontSize: 17, color: 'text.secondary' }} /></ListItemIcon>
              <ListItemText primary={<Typography sx={{ fontSize: 14 }}>Ajuda</Typography>} />
            </MenuItem>
          </Menu>
        </Box>

        {/* DataGrid mobile */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: 1, pb: 1 }}>
          <StyledDataGrid
            {...commonGridProps}
            sx={{
              width: '100%',
              border: 'none',
              borderRadius: 0,
              '& .MuiDataGrid-columnHeaderTitle': { fontSize: 12, fontWeight: 600 },
              '& .MuiDataGrid-cell': { borderBottom: `1px solid ${bdr}`, px: 1.5 },
              '& .MuiDataGrid-footerContainer': { minHeight: 44, borderTop: `1px solid ${bdr}` },
            }}
            columns={columnsMobile}
            rowHeight={44}
            columnHeaderHeight={36}
            initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
            pageSizeOptions={[20, 50, 100]}
            slots={{ ...commonGridProps.slots, toolbar: SearchToolbar }}
            slotProps={{ toolbar: { quickFilterProps: { debounceMs: 500 } } }}
          />
        </Box>
      </Box>
    )
  }

  // ─── Desktop ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {modals}

      <AppPageHeader
        title="Produtos"
        count={products.length}
        helpContent={<ProductsHelper />}
      />

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: 2, py: 1.5 }}>
        <StyledDataGrid
          {...commonGridProps}
          sx={{ width: '100%' }}
          columns={columnsDesktop}
          initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
          pageSizeOptions={[10, 20, 30]}
          density="compact"
          slots={{ ...commonGridProps.slots, toolbar: DesktopToolbar as ComponentType }}
          slotProps={{
            toolbar: {
              searchMode, onChangeSearchMode: setSearchMode,
              search, onChangeSearch: setSearch,
              tableFilter, onChangeTableFilter: setTableFilter,
              promoFilter, onChangePromoFilter: setPromoFilter,
              priceTables,
              hasModifications, onSave: handleSave, isSaving: isUpdating,
              hasSelection: !!selectedProducts.length,
              selectionCount: selectedProducts.length,
              onDelete: handleDelete, isDeleting,
              onBulkAssignTable: handleBulkAssignTable,
              onOpenImport: () => setOpenImport(true), onOpenCRUD: () => setOpenCRUD(true),
            } as DesktopToolbarProps,
          }}
        />
      </Box>
    </Box>
  )
}

export default ProductsPage
